import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { MapPin } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

interface MapPin {
  id: string;
  calendarId: string;
  calendarName: string;
  label: string;
  latitude: number;
  longitude: number;
  color: string;
  type: 'calendar' | 'event';
}

interface PopupState {
  pin: MapPin;
  x: number;
  y: number;
}

export default function GlobalCalendarMap() {
  const [pins, setPins] = useState<MapPin[]>([]);
  const [loading, setLoading] = useState(true);
  const [popup, setPopup] = useState<PopupState | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const leafletMapRef = useRef<any>(null);

  useEffect(() => {
    loadPins();
  }, []);

  useEffect(() => {
    if (!loading && pins.length > 0) {
      initMap();
    }
    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
  }, [loading, pins]);

  async function loadPins() {
    const [calendarRes, eventRes] = await Promise.all([
      supabase
        .from('calendars')
        .select('id, name, latitude, longitude, banner')
        .eq('is_public', true)
        .not('latitude', 'is', null)
        .not('longitude', 'is', null),
      supabase
        .from('calendar_events')
        .select('id, title, latitude, longitude, calendar_id, calendars!inner(name, banner, is_public)')
        .eq('calendars.is_public', true)
        .not('latitude', 'is', null)
        .not('longitude', 'is', null)
        .gte('start_datetime', new Date().toISOString()),
    ]);

    const collected: MapPin[] = [];

    for (const c of calendarRes.data ?? []) {
      const color = (c.banner as any)?.color || '#0ea5e9';
      collected.push({
        id: `cal-${c.id}`,
        calendarId: c.id,
        calendarName: c.name,
        label: c.name,
        latitude: c.latitude,
        longitude: c.longitude,
        color,
        type: 'calendar',
      });
    }

    const calendarIdsWithPin = new Set(collected.map(p => p.calendarId));

    for (const e of eventRes.data ?? []) {
      const cal = (e as any).calendars;
      const color = cal?.banner?.color || '#0ea5e9';
      collected.push({
        id: `evt-${e.id}`,
        calendarId: e.calendar_id,
        calendarName: cal?.name ?? '',
        label: e.title,
        latitude: e.latitude,
        longitude: e.longitude,
        color,
        type: 'event',
      });
      calendarIdsWithPin.add(e.calendar_id);
    }

    setPins(collected);
    setLoading(false);
  }

  async function initMap() {
    if (!mapContainerRef.current || leafletMapRef.current) return;

    const L = (await import('leaflet')).default;
    await import('leaflet.markercluster');

    const map = L.map(mapContainerRef.current, {
      center: [39, -98],
      zoom: 3,
      zoomControl: true,
      scrollWheelZoom: false,
      attributionControl: true,
    });

    leafletMapRef.current = map;

    L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
      subdomains: 'abcd',
      maxZoom: 19,
    }).addTo(map);

    setTimeout(() => map.invalidateSize(), 100);

    const clusterGroup = (L as any).markerClusterGroup({
      maxClusterRadius: 40,
      spiderfyOnMaxZoom: true,
      showCoverageOnHover: false,
      iconCreateFunction: (cluster: any) => {
        const count = cluster.getChildCount();
        return L.divIcon({
          html: `<div class="cal-map-cluster">${count}</div>`,
          className: '',
          iconSize: [36, 36],
        });
      },
    });

    pins.forEach(pin => {
      const size = pin.type === 'calendar' ? 16 : 11;
      const anchor = size / 2;
      const marker = L.marker([pin.latitude, pin.longitude], {
        icon: L.divIcon({
          html: `<div class="cal-map-dot ${pin.type === 'calendar' ? 'cal-map-dot--cal' : 'cal-map-dot--evt'}" style="background:${pin.color};box-shadow:0 0 0 3px ${pin.color}33,0 2px 6px rgba(0,0,0,0.15);width:${size}px;height:${size}px"></div>`,
          className: '',
          iconSize: [size, size],
          iconAnchor: [anchor, anchor],
        }),
      });

      marker.on('click', () => {
        const point = map.latLngToContainerPoint([pin.latitude, pin.longitude]);
        setPopup({ pin, x: point.x, y: point.y });
      });

      clusterGroup.addLayer(marker);
    });

    map.addLayer(clusterGroup);
    map.on('click', () => setPopup(null));
    map.on('movestart', () => setPopup(null));
  }

  const cityCount = new Set(
    pins.map(p => `${Math.round(p.latitude * 2) / 2},${Math.round(p.longitude * 2) / 2}`)
  ).size;

  return (
    <div className="bg-white border-t border-b border-gray-100 py-16">
      <style>{`
        .cal-map-dot {
          border-radius: 50%;
          border: 2px solid white;
          cursor: pointer;
          transition: transform 0.15s;
        }
        .cal-map-dot:hover { transform: scale(1.5); }
        .cal-map-cluster {
          width: 36px;
          height: 36px;
          border-radius: 50%;
          background: #0ea5e9;
          color: white;
          font-size: 13px;
          font-weight: 700;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px solid white;
          box-shadow: 0 2px 8px rgba(0,0,0,0.18);
        }
        .leaflet-container {
          background: #e8eef4 !important;
          font-family: inherit;
        }
        .leaflet-control-attribution {
          font-size: 10px !important;
          background: rgba(255,255,255,0.7) !important;
        }
        .marker-cluster-small,
        .marker-cluster-medium,
        .marker-cluster-large {
          background-color: rgba(14, 165, 233, 0.15) !important;
        }
        .marker-cluster-small div,
        .marker-cluster-medium div,
        .marker-cluster-large div {
          background-color: rgba(14, 165, 233, 0.85) !important;
          color: white !important;
        }
      `}</style>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-sky-50 text-sky-700 text-sm font-medium px-3 py-1 rounded-full mb-4">
            <MapPin className="h-3.5 w-3.5" />
            {loading ? '...' : `${cityCount} locations`}
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">
            Events Happening Near You
          </h2>
          <p className="text-lg text-gray-500 max-w-xl mx-auto">
            Explore calendars from cities around the world
          </p>
        </div>

        <div
          className="relative rounded-2xl overflow-hidden border border-gray-100 shadow-sm"
          style={{ height: 460 }}
        >
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-50 z-10">
              <div className="h-8 w-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }} />

          {popup && (
            <MapPopup
              pin={popup.pin}
              x={popup.x}
              y={popup.y}
              onClose={() => setPopup(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function MapPopup({
  pin,
  x,
  y,
  onClose,
}: {
  pin: MapPin;
  x: number;
  y: number;
  onClose: () => void;
}) {
  const POPUP_WIDTH = 230;
  const LEFT_OFFSET = 12;
  const TOP_OFFSET = -140;

  const containerEl = document.querySelector('.leaflet-container') as HTMLElement | null;
  const containerWidth = containerEl?.offsetWidth ?? 800;
  let left = x + LEFT_OFFSET;
  if (left + POPUP_WIDTH > containerWidth - 12) {
    left = x - POPUP_WIDTH - LEFT_OFFSET;
  }

  return (
    <div
      className="absolute z-[1000] bg-white rounded-xl shadow-xl border border-gray-100 p-4 text-left"
      style={{ left, top: y + TOP_OFFSET, width: POPUP_WIDTH, pointerEvents: 'all' }}
      onClick={e => e.stopPropagation()}
    >
      <div className="flex items-start gap-2 mb-1">
        <div
          className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1"
          style={{ background: pin.color }}
        />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2">
            {pin.label}
          </p>
          {pin.type === 'event' && (
            <p className="text-xs text-gray-400 mt-0.5 truncate">{pin.calendarName}</p>
          )}
        </div>
        <button
          onClick={onClose}
          className="text-gray-300 hover:text-gray-500 flex-shrink-0 text-lg leading-none -mt-0.5"
        >
          &times;
        </button>
      </div>

      <div className="flex justify-end mt-3">
        <Link
          to={`/calendar/${pin.calendarId}`}
          className="text-xs font-medium text-sky-600 hover:text-sky-700 bg-sky-50 hover:bg-sky-100 px-3 py-1.5 rounded-lg transition-colors"
        >
          View calendar
        </Link>
      </div>
    </div>
  );
}
